/**
 * Deterministic sequence-diagram layout (NO dagre).
 *
 * Participants become fixed columns (x); the ordered event list is walked with a
 * row cursor (y), so messages are time-ordered top→down. Messages are straight
 * `CustomEdge`s pinned to per-row lifeline handles via the shared
 * `sequenceRowHandleId` keystone (render side == reference side → no dropped
 * edges). Activations, fragments (nestable), and notes are computed in the same
 * pass. Lifelines, fragment boxes, and notes are emitted as nodes selected by the
 * `sequenceContent` / `sequenceFragment` / `sequenceNote` content fields.
 */

import type { DiagramData, DiagramNode, DiagramEdge } from '../types/schema'
import type { SequenceModel } from './mermaidSequence'
import { sequenceRowHandleId } from './sequenceHandles'

const COL_W = 180
const PAD_X = 90
const BOTTOM_PAD = 28

// Exported so SequenceLifelineNode renders handles / activation bars / the
// lifeline at exactly the y's the layout used (keeps node + layout in lockstep).
export const SEQ_LIFE_W = 14
export const SEQ_HEADER_H = 72
export const SEQ_ROW_H = 46
const LIFE_W = SEQ_LIFE_W
const HEADER_H = SEQ_HEADER_H
const ROW_H = SEQ_ROW_H

const xCenter = (col: number) => PAD_X + col * COL_W
/** Y-center of an event row (matches the lifeline node's handle/bar positions). */
export const seqRowY = (row: number) => HEADER_H + row * ROW_H + ROW_H / 2
const yRow = seqRowY

type Side = 'left' | 'right'

const ARROW_END: Record<'sync' | 'async' | 'reply' | 'lost', string> = {
  sync: 'arrow',
  async: 'open-arrow',
  reply: 'arrow',
  lost: 'cross',
}

export function buildSequenceDiagram(model: SequenceModel): DiagramData {
  const { participants, events, autonumber } = model
  const colOf = new Map<string, number>()
  participants.forEach((p, i) => colOf.set(p.id, i))

  const nodes: DiagramNode[] = []
  const edges: DiagramEdge[] = []

  // Per-participant activation stack + emitted bars.
  const actStack = new Map<string, number[]>() // participant -> stack of startRows
  const activations = new Map<string, Array<{ startRow: number; endRow: number; depth?: number }>>()
  participants.forEach((p) => { actStack.set(p.id, []); activations.set(p.id, []) })
  const openAct = (id: string, row: number) => { actStack.get(id)!.push(row) }
  const closeAct = (id: string, row: number) => {
    const st = actStack.get(id)
    if (!st || !st.length) return
    const startRow = st.pop()!
    activations.get(id)!.push({ startRow, endRow: row, depth: st.length })
  }

  // Fragment frame stack.
  interface Frame { kind: any; label?: string; startRow: number; minCol: number; maxCol: number; sections: Array<{ label?: string; atRow: number }> }
  const fragStack: Frame[] = []
  const fragments: Array<Frame & { endRow: number; depth: number }> = []
  const touchCols = (...cols: number[]) => {
    for (const f of fragStack) {
      f.minCol = Math.min(f.minCol, ...cols)
      f.maxCol = Math.max(f.maxCol, ...cols)
    }
  }

  let row = 0
  let msgNo = 0

  for (const ev of events) {
    if (ev.t === 'activate') { openAct(ev.part, row); continue }
    if (ev.t === 'deactivate') { closeAct(ev.part, row); continue }

    if (ev.t === 'fragStart') {
      fragStack.push({ kind: ev.kind, label: ev.label, startRow: row, minCol: Infinity, maxCol: -Infinity, sections: [] })
      row += 1 // label band
      continue
    }
    if (ev.t === 'fragSection') {
      const f = fragStack[fragStack.length - 1]
      if (f) f.sections.push({ label: ev.label, atRow: row })
      row += 1 // divider band
      continue
    }
    if (ev.t === 'fragEnd') {
      const f = fragStack.pop()
      if (f) {
        const endRow = row
        row += 1 // bottom band
        if (!Number.isFinite(f.minCol)) { f.minCol = 0; f.maxCol = participants.length - 1 }
        fragments.push({ ...f, endRow, depth: fragStack.length })
      }
      continue
    }

    if (ev.t === 'note') {
      const cols = ev.parts.map((p) => colOf.get(p) ?? 0)
      touchCols(...cols)
      const minC = Math.min(...cols), maxC = Math.max(...cols)
      let nx: number, nw: number
      if (ev.placement === 'over') { nx = xCenter(minC) - COL_W * 0.4; nw = (maxC - minC) * COL_W + COL_W * 0.8 }
      else if (ev.placement === 'left') { nx = xCenter(minC) - COL_W * 0.9; nw = COL_W * 0.8 }
      else { nx = xCenter(maxC) + COL_W * 0.1; nw = COL_W * 0.8 }
      nodes.push({
        id: `seq-note-${row}`,
        nodeType: 'component',
        label: ev.text,
        position: { x: nx, y: yRow(row) - ROW_H * 0.35 },
        size: { width: nw, height: ROW_H * 0.7 },
        sequenceNote: { text: ev.text, placement: ev.placement },
      } as DiagramNode)
      row += 1
      continue
    }

    if (ev.t === 'msg') {
      const fromCol = colOf.get(ev.from) ?? 0
      const toCol = colOf.get(ev.to) ?? 0
      touchCols(fromCol, toCol)
      const label = autonumber ? `${++msgNo}. ${ev.label}`.trim() : ev.label
      const baseData: any = {
        arrowEnd: ARROW_END[ev.arrow],
        color: '#475569',
        fixedConnection: true,
        lineStyle: ev.arrow === 'reply' ? 'dashed' : 'solid',
      }

      if (fromCol === toCol) {
        // self-message: 2-row right-side loop
        edges.push({
          id: `seq-msg-${edges.length}`,
          source: `seq-life-${ev.from}`,
          target: `seq-life-${ev.to}`,
          sourceHandle: sequenceRowHandleId(row, 'right'),
          targetHandle: sequenceRowHandleId(row + 1, 'right'),
          label,
          type: 'custom',
          edgeType: 'message',
          data: { ...baseData, pathStyle: 'smoothstep' },
        } as DiagramEdge)
        if (ev.activate) openAct(ev.to, row)
        if (ev.deactivate) closeAct(ev.from, row)
        row += 2
        continue
      }

      const sourceSide: Side = toCol > fromCol ? 'right' : 'left'
      const targetSide: Side = toCol > fromCol ? 'left' : 'right'
      edges.push({
        id: `seq-msg-${edges.length}`,
        source: `seq-life-${ev.from}`,
        target: `seq-life-${ev.to}`,
        sourceHandle: sequenceRowHandleId(row, sourceSide),
        targetHandle: sequenceRowHandleId(row, targetSide),
        label,
        type: 'custom',
        edgeType: 'message',
        data: { ...baseData, pathStyle: 'straight' },
      } as DiagramEdge)
      if (ev.activate) openAct(ev.to, row)
      if (ev.deactivate) closeAct(ev.from, row)
      row += 1
      continue
    }
  }

  // Close any dangling activations / fragments at the final row.
  for (const p of participants) while (actStack.get(p.id)!.length) closeAct(p.id, row)
  while (fragStack.length) {
    const f = fragStack.pop()!
    if (!Number.isFinite(f.minCol)) { f.minCol = 0; f.maxCol = participants.length - 1 }
    fragments.push({ ...f, endRow: row, depth: fragStack.length })
    row += 1
  }

  const rowCount = Math.max(row, 1)
  const totalHeight = HEADER_H + rowCount * ROW_H + BOTTOM_PAD

  // Lifeline nodes (one per participant).
  participants.forEach((p, c) => {
    nodes.push({
      id: `seq-life-${p.id}`,
      nodeType: 'component',
      label: p.name,
      position: { x: xCenter(c) - LIFE_W / 2, y: 0 },
      size: { width: LIFE_W, height: totalHeight },
      sequenceContent: {
        participantName: p.name,
        kind: p.kind,
        rowCount,
        activations: activations.get(p.id)!,
      },
    } as DiagramNode)
  })

  // Fragment box nodes (span enclosed columns × rows, inset by nesting depth).
  fragments.forEach((f, i) => {
    const inset = f.depth * 6
    const left = xCenter(f.minCol) - COL_W * 0.45 + inset
    const right = xCenter(f.maxCol) + COL_W * 0.45 - inset
    const top = yRow(f.startRow) - ROW_H * 0.4
    const bottom = yRow(f.endRow) + ROW_H * 0.4
    nodes.push({
      id: `seq-frag-${i}`,
      nodeType: 'component',
      label: f.label ?? f.kind,
      position: { x: left, y: top },
      size: { width: Math.max(right - left, COL_W), height: Math.max(bottom - top, ROW_H) },
      zIndex: -5 - f.depth,
      sequenceFragment: { kind: f.kind, label: f.label, sections: f.sections },
    } as DiagramNode)
  })

  return { version: '1.0', diagramType: 'sequence', nodes, edges } as DiagramData
}
